import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import { ArrowRight, Smartphone } from "lucide-react";

/**
 * Scan-to-connect QR block for print collateral, event tables, and
 * business cards. Reads window.location.origin at runtime so the QR
 * always resolves to whichever host the site is served from (preview,
 * production, or a future custom domain).
 */
export default function ScanToConnect({
  path = "/contact",
  eyebrow = "Scan · Share · Connect",
  headline = "Take WHO with you.",
  body = "Point your phone camera at the code to open WHO instantly. Perfect for veteran service tables, transition briefings, funder meetings, and community events.",
}) {
  const site = typeof window !== "undefined" ? window.location.origin : "https://vet-connect-25.emergent.host";
  const url = `${site}${path}`;

  return (
    <section className="py-20 lg:py-24 bg-cream-panel border-y border-borderSubtle" data-testid="scan-to-connect">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow text-gold-deep">{eyebrow}</p>
            <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.05] text-3xl md:text-4xl lg:text-5xl">
              {headline}
            </h2>
            <p className="mt-5 text-lg text-slateSoft leading-relaxed max-w-2xl">
              {body}
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Open the site instantly — no typing a URL",
                "Great for printed one-pagers, banners, and community events",
                "Shareable at VSO meetings, universities, and public agencies",
              ].map((it) => (
                <li key={it} className="flex gap-3 text-navy" data-testid={`scan-benefit-${it.toLowerCase().replace(/\W+/g, "-").slice(0, 40)}`}>
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-gold" />
                  {it}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="scan-cta-contact"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3.5 rounded-md transition-colors"
              >
                Contact WHO <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </Link>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="scan-cta-open"
                className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-cream font-semibold px-6 py-3.5 rounded-md transition-colors"
              >
                <Smartphone className="w-4 h-4" /> Open link
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 flex justify-center">
            <div className="relative bg-cream p-6 md:p-8 rounded-xl border border-gold/40 shadow-xl" data-testid="scan-qr-card">
              <div className="absolute -top-3 left-6 px-2.5 py-1 rounded-md bg-navy text-cream text-[10px] font-bold tracking-widest uppercase">
                WHO · Scan
              </div>
              <QRCodeSVG
                value={url}
                size={240}
                level="H"
                marginSize={0}
                bgColor="#F6F1E6"
                fgColor="#0F2138"
                data-testid="scan-qr-svg"
              />
              <p className="mt-5 text-center text-xs uppercase tracking-widest text-slateSoft break-all">
                {url.replace(/^https?:\/\//, "")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
